import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

export default function CommissionCalc() {
  return (
    <CalcShell look="cnet" title="Commission Calculator" subtitle="Compute the commission owed given a sales price and commission rate, or a tiered structure."
      about={<><p>The <b>Commission Calculator</b> supports both simple percentage commissions and tiered commission structures (common for real estate and sales roles).</p></>}
    >
      <SimpleCommission/>
      <hr className="my-8 border-slate-200"/>
      <TieredCommission/>
    </CalcShell>
  );
}

function SimpleCommission() {
  const [salesPrice, setSalesPrice] = useState(200000);
  const [rate, setRate] = useState(3);
  const [commission, setCommission] = useState('');
  const [solveFor, setSolveFor] = useState('commission'); // 'commission' | 'sales' | 'rate'

  const c = useMemo(() => {
    const sp = +salesPrice || 0, r = +rate || 0, cm = +commission || 0;
    if (solveFor === 'commission') return { salesPrice: sp, rate: r, commission: sp * r / 100 };
    if (solveFor === 'sales')      return { salesPrice: r > 0 ? cm * 100 / r : 0, rate: r, commission: cm };
    return { salesPrice: sp, rate: sp > 0 ? cm * 100 / sp : 0, commission: cm };
  }, [salesPrice, rate, commission, solveFor]);

  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-2">Commission Calculator</h3>
      <p className="text-sm text-slate-600 mb-4">Compute any one of the following, given inputs for the remaining two: sales price, commission rate, or commission.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Solve For</label>
            <select value={solveFor} onChange={e=>setSolveFor(e.target.value)} data-testid="comm-solve">
              <option value="commission">Commission ($)</option>
              <option value="sales">Sales Price</option>
              <option value="rate">Commission Rate</option>
            </select>
          </div>
          {solveFor !== 'sales' && <div className="field"><label>Sales Price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={salesPrice} onChange={e=>setSalesPrice(+e.target.value||0)} className="flex-1" data-testid="comm-sales"/></div></div>}
          {solveFor !== 'rate' && <div className="field"><label>Commission Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1" data-testid="comm-rate"/><span className="self-center text-slate-500">%</span></div></div>}
          {solveFor !== 'commission' && <div className="field"><label>Commission</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" step="0.01" value={commission} onChange={e=>setCommission(+e.target.value||0)} className="flex-1"/></div></div>}
        </div>
        <div>
          <div className="cnet-result-hdr">Result</div>
          <div className="cnet-result-body">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b"><td className="py-2">Sales Price</td><td className={`text-right ${solveFor==='sales'?'font-bold text-blue-700':''}`}>{fmt(c.salesPrice)}</td></tr>
                <tr className="border-b"><td className="py-2">Commission Rate</td><td className={`text-right ${solveFor==='rate'?'font-bold text-blue-700':''}`}>{c.rate.toFixed(3)}%</td></tr>
                <tr><td className="py-2">Commission</td><td className={`text-right ${solveFor==='commission'?'font-bold text-blue-700':''}`} data-testid="comm-result">{fmt(c.commission)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function TieredCommission() {
  const [sales, setSales] = useState(200000);
  const [hasBase, setHasBase] = useState('no');
  const [baseAmt, setBaseAmt] = useState(0);
  const [varies, setVaries] = useState('yes');
  const [flatRate, setFlatRate] = useState(3);
  const [flatMode, setFlatMode] = useState('%');
  const [tiers, setTiers] = useState([
    { to: 20000, rate: 3, mode: '%' },
    { to: '', rate: 5, mode: '%' },
    { to: '', rate: 0, mode: '%' },
    { to: '', rate: 0, mode: '%' },
    { to: '', rate: 0, mode: '%' },
    { to: '', rate: 0, mode: '%' },
    { to: '', rate: 0, mode: '%' },
    { to: '', rate: 0, mode: '%' },
    { to: '', rate: 0, mode: '%' },
  ]);
  const c = useMemo(() => {
    let total = hasBase === 'yes' ? baseAmt : 0;
    const rows = [];
    if (varies === 'no') {
      const comm = flatMode === '%' ? sales * flatRate/100 : flatRate;
      rows.push({ from: 0, to: sales, sales, rate: flatRate, mode: flatMode, commission: comm });
      total += comm;
    } else {
      let prev = 0;
      let remaining = sales;
      for (const t of tiers) {
        if (t.rate === 0 && t.to === '') break;
        const upper = t.to === '' || t.to === null ? sales : Math.min(+t.to, sales);
        if (upper <= prev) continue;
        const inTier = Math.max(0, Math.min(remaining, upper - prev));
        const comm = t.mode === '%' ? inTier * t.rate/100 : (inTier > 0 ? +t.rate : 0);
        rows.push({ from: prev, to: upper, sales: inTier, rate: t.rate, mode: t.mode, commission: comm });
        total += comm;
        remaining -= inTier;
        prev = upper;
        if (remaining <= 0) break;
      }
    }
    return { total, rows };
  }, [sales, hasBase, baseAmt, varies, flatRate, flatMode, tiers]);
  const updateTier = (i, patch) => setTiers(prev => prev.map((t,j)=>j===i?{...t,...patch}:t));
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Tiered Commission Calculator</h3>
      <p className="text-sm text-slate-600 mb-4">This calculator can calculate more complex commission structures, including tiered commissions and commissions that include a base amount.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Sales Price</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={sales} onChange={e=>setSales(+e.target.value||0)} className="flex-1" data-testid="comm-tier-sales"/></div></div>
          <div className="field"><label>Has a base commission?</label>
            <div className="flex gap-3 text-sm">
              <label className="flex items-center gap-1"><input type="radio" name="hb" checked={hasBase==='yes'} onChange={()=>setHasBase('yes')} data-testid="comm-tier-base-yes"/> Yes</label>
              <label className="flex items-center gap-1"><input type="radio" name="hb" checked={hasBase==='no'} onChange={()=>setHasBase('no')}/> No</label>
            </div>
            {hasBase === 'yes' && <div className="flex gap-2 mt-2"><span className="self-center text-slate-500">$</span><input type="number" value={baseAmt} onChange={e=>setBaseAmt(+e.target.value||0)} className="flex-1" data-testid="comm-tier-base-amt"/></div>}
          </div>
          <div className="field"><label>Commission varies with price?</label>
            <div className="flex gap-3 text-sm">
              <label className="flex items-center gap-1"><input type="radio" name="cv" checked={varies==='yes'} onChange={()=>setVaries('yes')}/> Yes</label>
              <label className="flex items-center gap-1"><input type="radio" name="cv" checked={varies==='no'} onChange={()=>setVaries('no')}/> No</label>
            </div>
          </div>
          {varies === 'no' && (
            <div className="field"><label>Commission Rate</label>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <input type="number" step="0.01" value={flatRate} onChange={e=>setFlatRate(+e.target.value||0)}/>
                <select value={flatMode} onChange={e=>setFlatMode(e.target.value)}><option value="%">%</option><option value="$">$</option></select>
              </div>
            </div>
          )}
          {varies === 'yes' && (
            <div className="mt-3">
              <div className="text-sm text-slate-700 mb-2">Please define the tiered commission structure below. Leave the "To" value blank if there is no upper limit.</div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-700"><th>From:</th><th>To:</th><th>Commission</th></tr></thead>
                <tbody>
                  {tiers.map((t, i) => {
                    const from = i === 0 ? 0 : (tiers[i-1].to === '' ? 0 : +tiers[i-1].to);
                    return (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1 pr-2 text-slate-700 whitespace-nowrap">${from.toLocaleString()}</td>
                        <td className="py-1 pr-2">
                          <div className="flex gap-1"><span className="self-center">$</span><input type="number" value={t.to} onChange={e=>updateTier(i,{to: e.target.value})} className="w-28 text-right border border-slate-300 rounded px-1"/></div>
                        </td>
                        <td className="py-1 flex gap-1">
                          <input type="number" step="0.01" value={t.rate} onChange={e=>updateTier(i,{rate: +e.target.value||0})} className="w-14 text-right border border-slate-300 rounded px-1"/>
                          <span className="self-center text-slate-500">%</span>
                          <select value={t.mode} onChange={e=>updateTier(i,{mode: e.target.value})} className="border border-slate-300 rounded"><option value="%">%</option><option value="$">$</option></select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div>
          <div className="cnet-result-hdr">Result</div>
          <div className="cnet-result-body">
            <div className="mb-3">
              <div className="text-sm text-slate-500">Total Commission</div>
              <div className="text-3xl font-bold text-emerald-700" data-testid="comm-tier-total">{fmt(c.total)}</div>
            </div>
            {c.rows.length > 0 && (
              <table className="w-full text-sm border-collapse">
                <thead className="bg-slate-100"><tr><th className="px-2 py-1 text-left">Bracket</th><th className="px-2 py-1 text-right">Sales</th><th className="px-2 py-1 text-right">Rate</th><th className="px-2 py-1 text-right">Commission</th></tr></thead>
                <tbody>
                  {c.rows.map((r,i)=>(<tr key={i} className="border-t border-slate-200"><td className="px-2 py-1">${r.from.toLocaleString()} – ${r.to.toLocaleString()}</td><td className="px-2 py-1 text-right">{fmt(r.sales)}</td><td className="px-2 py-1 text-right">{r.rate}{r.mode}</td><td className="px-2 py-1 text-right font-semibold">{fmt(r.commission)}</td></tr>))}
                  {hasBase === 'yes' && baseAmt > 0 && <tr className="border-t border-slate-200"><td className="px-2 py-1 italic">Base commission</td><td className="px-2 py-1"></td><td className="px-2 py-1"></td><td className="px-2 py-1 text-right font-semibold">{fmt(baseAmt)}</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
